import { useState } from 'react';
import { toast } from 'sonner';
import type { App } from '@hangar/shared';
import type { SigningCredential } from '@/api/endpointsSigning';
import { useSigningMutations } from '@/api/hooksSigning';
import { uploadProblem, type SigningSlot } from './signingModel';

/** What the upload dialog edits: a new credential for the slot, or a rotation of the existing one (same id). */
export interface SigningUploadTarget {
  app: App;
  slot: SigningSlot;
  current: SigningCredential | null;
}

/** The upload form's state and its submit (platform operators only; the API refuses anyone else with 403). For an Android keystore `password` is the keystore password. */
export function useSigningUpload(projectId: string, target: SigningUploadTarget, onDone: () => void) {
  const { save } = useSigningMutations(projectId);
  const [file, setFile] = useState<File | null>(null);
  const [password, setPassword] = useState('');
  const [keyId, setKeyId] = useState(typeof target.current?.metadata.keyId === 'string' ? target.current.metadata.keyId : '');
  const [issuerId, setIssuerId] = useState(typeof target.current?.metadata.issuerId === 'string' ? target.current.metadata.issuerId : '');
  const [keyAlias, setKeyAlias] = useState(typeof target.current?.metadata.keyAlias === 'string' ? target.current.metadata.keyAlias : '');
  const [keyPassword, setKeyPassword] = useState('');
  const [applicationId, setApplicationId] = useState(typeof target.current?.metadata.applicationId === 'string' ? target.current.metadata.applicationId : '');
  const [acknowledge, setAcknowledge] = useState(false);
  const [idempotencyKey] = useState(() => crypto.randomUUID());
  const [submitted, setSubmitted] = useState(false);
  const problem = uploadProblem(target.slot, { file, password, keyId, issuerId, keyAlias, applicationId });

  const submit = async () => {
    setSubmitted(true);
    if (problem || !file) return;
    const keystore = target.slot.needsKeystore ? { keystorePassword: password, keyAlias, keyPassword, applicationId } : {};
    const upload = { file, ...(target.slot.needsPassword ? { password } : {}), ...(target.slot.needsKeyIds ? { keyId, issuerId } : {}), ...keystore, acknowledgeCertificateChange: acknowledge };
    const action = target.current
      ? ({ type: 'rotate', credentialId: target.current.id, upload } as const)
      : ({ type: 'upload', kind: target.slot.kind, appId: target.app.id, name: `${target.app.name} · ${target.slot.label}`, upload, idempotencyKey } as const);
    try {
      await save.mutateAsync(action);
      toast.success(target.current ? `${target.slot.label} replaced` : `${target.slot.label} stored`);
      onDone();
    } catch {
      // the dialog shows the problem (ProblemAlert reads save.error)
    }
  };

  return {
    file, setFile, password, setPassword, keyId, setKeyId, issuerId, setIssuerId, keyAlias, setKeyAlias, keyPassword, setKeyPassword, applicationId, setApplicationId, acknowledge, setAcknowledge,
    problem: submitted ? problem : null, error: save.error, pending: save.isPending, submit,
  };
}

export type SigningUploadApi = ReturnType<typeof useSigningUpload>;
